// Client helper that calls our server-side /api/openai proxy. The browser
// never sees the OpenAI API key — it lives in OPENAI_API_KEY on the server.
//
// When `caseId` is provided, the case's master-context markdown (see
// getCaseContext in lib/cases) is prepended to the system prompt on every
// completion, so the model answers from the authoritative case record.
//
// Streaming: pass `onChunk` to receive incremental deltas as they arrive.
// The Promise still resolves with the full concatenated text. Streaming is
// enabled automatically whenever `onChunk` is provided.

import { getCaseContext } from './cases';

export async function aiCall({
  system,
  messages,
  max_tokens = 1200,
  model,
  caseId,
  signal,
  onChunk,
}) {
  const ctx = caseId ? getCaseContext(caseId) : null;
  const systemParts = [];
  if (ctx) systemParts.push(ctx);
  if (system) systemParts.push(system);
  const finalSystem = systemParts.join('\n\n---\n\n');

  const all = finalSystem
    ? [{ role: 'system', content: finalSystem }, ...messages]
    : messages;

  const stream = !!onChunk;
  const r = await fetch('/api/openai', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages: all, max_tokens, model, stream }),
    signal,
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(`/api/openai ${r.status}: ${txt || r.statusText}`);
  }

  if (!stream) {
    const data = await r.json();
    if (data.error) throw new Error(data.error);
    return data.content || '';
  }

  const reader = r.body && r.body.getReader ? r.body.getReader() : null;
  if (!reader) {
    const fallback = await r.text();
    if (fallback && onChunk) onChunk(fallback, fallback);
    return fallback;
  }
  const decoder = new TextDecoder();
  let buffer = '';
  let full = '';
  let errored = null;

  const handle = (line) => {
    if (!line) return;
    try {
      const evt = JSON.parse(line);
      if (evt.type === 'delta') {
        full += evt.text;
        onChunk(evt.text, full);
      } else if (evt.type === 'error') {
        errored = evt.error;
      }
    } catch {
      // tolerate malformed lines
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl;
    while ((nl = buffer.indexOf('\n')) !== -1) {
      handle(buffer.slice(0, nl).trim());
      buffer = buffer.slice(nl + 1);
    }
  }
  if (buffer.trim()) handle(buffer.trim());
  if (errored) throw new Error(errored);
  return full;
}
